import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ContributionAdminService } from 'src/app/shared/services/contribution-admin.service';
import {
  Contribution,
  ContributionStatus,
  PaymentMethod,
  OdooSyncStatus,
} from 'src/app/shared/models/contribution.models';
import { BreadcrumbService } from '../../services/breadcrumb.service';

@Component({
  selector: 'app-contribution-detail',
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    ToastModule,
    TableModule,
    TextareaModule,
    TooltipModule,
    FormsModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './contribution-detail.component.html',
})
export class ContributionDetailComponent implements OnInit {
  contributionService = inject(ContributionAdminService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  notificationService = inject(NotificationService);
  confirmationService = inject(ConfirmationService);
  breadcrumbService = inject(BreadcrumbService);

  contribution: Contribution | null = null;
  loading = true;
  savingNotes = false;
  markingAsPaid = false;
  retryingOdooSync = false;
  newNotes = '';

  // Expose enums to template
  ContributionStatus = ContributionStatus;
  PaymentMethod = PaymentMethod;
  OdooSyncStatus = OdooSyncStatus;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadContribution(id);
    }
  }

  loadContribution(id: string): void {
    this.loading = true;
    this.contributionService.getContribution(id).subscribe({
      next: (response) => {
        this.contribution = response.data;
        this.loading = false;

        // Set breadcrumb label
        if (this.contribution?.id) {
          const primaryEmail = this.contribution.user?.emails?.find(e => e.isPrimary)?.email
            || this.contribution.user?.emails?.[0]?.email;
          const userName = this.contribution.user
            ? `${this.contribution.user.firstName || ''} ${this.contribution.user.lastName || ''}`.trim() ||
              primaryEmail
            : null;
          const displayLabel = userName
            ? `${userName} - ${this.contribution.id.substring(0, 8)}`
            : this.contribution.id.substring(0, 8);
          this.breadcrumbService.setLabel(this.contribution.id, displayLabel);
        }
      },
      error: () => {
        this.notificationService.error('Failed to load contribution');
        this.loading = false;
        this.router.navigate(['/admin/contributions']);
      },
    });
  }

  saveAdminNotes(): void {
    if (!this.contribution || !this.newNotes.trim()) {
      return;
    }

    this.savingNotes = true;
    this.contributionService
      .updateAdminNotes(this.contribution.id, this.newNotes)
      .subscribe({
        next: (response) => {
          this.contribution = response.data;
          this.newNotes = '';
          this.savingNotes = false;
          this.notificationService.success('Admin notes updated successfully');
        },
        error: () => {
          this.notificationService.error('Failed to update notes');
          this.savingNotes = false;
        },
      });
  }

  markAsPaid(): void {
    if (!this.contribution) {
      return;
    }

    this.confirmationService.confirm({
      message:
        'Are you sure you want to mark this contribution as paid? This will generate a certificate and send a confirmation email to the user.',
      header: 'Confirm Payment Validation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, mark as paid',
      rejectLabel: 'Cancel',
      accept: () => this.executeMarkAsPaid(),
    });
  }

  private executeMarkAsPaid(): void {
    if (!this.contribution) {
      return;
    }

    this.markingAsPaid = true;
    this.contributionService
      .markAsPaid(this.contribution.id, 'Marked as paid by admin via UI')
      .subscribe({
        next: () => {
          this.markingAsPaid = false;
          this.notificationService.success('Contribution marked as paid');
          // Reload to get full contribution with all relations
          this.loadContribution(this.contribution!.id);
        },
        error: (err) => {
          this.notificationService.error(
            err.error?.message || 'Failed to mark as paid'
          );
          this.markingAsPaid = false;
        },
      });
  }

  backToList(): void {
    this.router.navigate(['/admin/contributions']);
  }

  getStatusSeverity(
    status: ContributionStatus,
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const severityMap: Record<
      ContributionStatus,
      'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast'
    > = {
      [ContributionStatus.PENDING_PAYMENT]: 'warn',
      [ContributionStatus.PAID]: 'success',
      [ContributionStatus.FAILED]: 'danger',
      [ContributionStatus.CANCELED]: 'secondary',
      [ContributionStatus.REFUNDED]: 'info',
    };
    return severityMap[status] || 'secondary';
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getStatusLabel(status: ContributionStatus): string {
    const labels: Record<ContributionStatus, string> = {
      [ContributionStatus.PENDING_PAYMENT]: 'Pending Payment',
      [ContributionStatus.PAID]: 'Paid',
      [ContributionStatus.FAILED]: 'Failed',
      [ContributionStatus.CANCELED]: 'Canceled',
      [ContributionStatus.REFUNDED]: 'Refunded',
    };
    return labels[status] || status;
  }

  getPaymentMethodLabel(method: PaymentMethod): string {
    const labels: Record<PaymentMethod, string> = {
      [PaymentMethod.CARD]: 'Card',
      [PaymentMethod.BANK_TRANSFER]: 'Bank Transfer',
    };
    return labels[method] || method;
  }

  getStatusClasses(status: ContributionStatus): string {
    const classes: Record<ContributionStatus, string> = {
      [ContributionStatus.PENDING_PAYMENT]: 'bg-warning-100 text-warning-700',
      [ContributionStatus.PAID]: 'bg-validation-100 text-validation-700',
      [ContributionStatus.FAILED]: 'bg-error-100 text-error-700',
      [ContributionStatus.CANCELED]: 'bg-greyscale-100 text-greyscale-700',
      [ContributionStatus.REFUNDED]: 'bg-primary-100 text-primary-700',
    };
    return classes[status] || 'bg-greyscale-100 text-greyscale-700';
  }

  getPaymentMethodClasses(method: PaymentMethod): string {
    const classes: Record<PaymentMethod, string> = {
      [PaymentMethod.CARD]: 'bg-primary-100 text-primary-700',
      [PaymentMethod.BANK_TRANSFER]: 'bg-greyscale-100 text-greyscale-700',
    };
    return classes[method] || 'bg-greyscale-100 text-greyscale-700';
  }

  formatAmount(amount: number | string): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `€${numAmount.toFixed(2)}`;
  }

  downloadCertificate(): void {
    if (!this.contribution) return;
    const url = this.contributionService.getCertificateDownloadUrl(
      this.contribution.id
    );
    window.open(url, '_blank');
  }

  downloadInvoice(): void {
    if (!this.contribution) return;
    if (!this.contribution.invoiceS3Key) {
      this.notificationService.info(
        'Invoice is being generated, please try again shortly'
      );
      return;
    }
    const url = this.contributionService.getInvoiceDownloadUrl(
      this.contribution.id
    );
    window.open(url, '_blank');
  }

  retryOdooSync(): void {
    if (!this.contribution) return;

    this.retryingOdooSync = true;
    this.contributionService.retryOdooSync(this.contribution.id).subscribe({
      next: () => {
        this.retryingOdooSync = false;
        this.notificationService.success(
          'Odoo sync job dispatched. Refresh the page in a few seconds.'
        );
        // Reload contribution after a short delay to show updated status
        setTimeout(() => {
          this.loadContribution(this.contribution!.id);
        }, 3000);
      },
      error: (err) => {
        this.retryingOdooSync = false;
        this.notificationService.error(
          err.error?.message || 'Failed to retry Odoo sync'
        );
      },
    });
  }

  isOdooSyncFailed(): boolean {
    return this.contribution?.odooSyncStatus === OdooSyncStatus.FAILED;
  }

  getTotalCO2(): number {
    if (!this.contribution) return 0;
    return this.contribution.projectContributions.reduce(
      (sum, pc) => sum + Number(pc.co2Compensated),
      0
    );
  }

  getShortId(): string {
    return this.contribution?.id.substring(0, 8) || '';
  }

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.notificationService.success('Copied to clipboard');
    });
  }

  isPaid(): boolean {
    return this.contribution?.status === ContributionStatus.PAID;
  }

  isPendingBankTransfer(): boolean {
    return (
      this.contribution?.status === ContributionStatus.PENDING_PAYMENT &&
      this.contribution?.paymentMethod === PaymentMethod.BANK_TRANSFER
    );
  }

  getSubtotal(): number {
    if (!this.contribution) return 0;
    const total =
      typeof this.contribution.totalAmount === 'string'
        ? parseFloat(this.contribution.totalAmount)
        : this.contribution.totalAmount;
    // VAT is 21%, so subtotal = total / 1.21
    return total / 1.21;
  }

  getVatAmount(): number {
    if (!this.contribution) return 0;
    const total =
      typeof this.contribution.totalAmount === 'string'
        ? parseFloat(this.contribution.totalAmount)
        : this.contribution.totalAmount;
    return total - total / 1.21;
  }

  getUserEmail(): string {
    if (!this.contribution?.user?.emails?.length) return '--';
    const primaryEmail = this.contribution.user.emails.find(e => e.isPrimary);
    return primaryEmail?.email || this.contribution.user.emails[0]?.email || '--';
  }
}
